/**
 * Newsletter form validation (popup + CTA).
 *
 * Validates email presence + format and — if the form contains newsletter
 * preference checkboxes — requires at least one of them to be checked
 * before the Mailchimp form is submitted.
 *
 * When the form carries a data-etp-mailchimp-landing URL, the submit is
 * sent via Mailchimp's JSONP endpoint and the browser is redirected to
 * that URL once Mailchimp responds (success and "already subscribed" are
 * treated identically). On network failure / timeout we fall back to the
 * native form post so the subscription is never lost.
 *
 * @param {Document|Element} scope
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const JSONP_TIMEOUT_MS = 8000;
let jsonpCallbackCounter = 0;

export function initNewsletterSignup(scope) {
  const forms = scope.querySelectorAll('.etp-newsletter-form-validate');
  forms.forEach((form) => {
    if (form.dataset.etpNlBound === '1') return;
    form.dataset.etpNlBound = '1';

    form.addEventListener('submit', (e) => {
      const error = validate(form);
      if (error) {
        e.preventDefault();
        showError(form, error);
        return;
      }
      clearError(form);

      const landing = form.dataset.etpMailchimpLanding;
      if (landing && !form.dataset.etpNlJsonpFallback) {
        e.preventDefault();
        submitViaJsonp(form, landing);
      }
    });

    form
      .querySelectorAll('input[type="checkbox"], input[type="email"]')
      .forEach((input) => {
        input.addEventListener('change', () => clearError(form));
        input.addEventListener('input', () => clearError(form));
      });
  });
}

function submitViaJsonp(form, landing) {
  const action = form.getAttribute('action') || '';
  if (!/\/subscribe\/post\?/.test(action)) {
    nativeSubmit(form);
    return;
  }

  const callbackName = '__etpMcCb' + (++jsonpCallbackCounter) + '_' + Date.now();
  const endpoint = action.replace('/subscribe/post?', '/subscribe/post-json?');

  const params = new URLSearchParams();
  new FormData(form).forEach((value, key) => {
    params.append(key, typeof value === 'string' ? value : '');
  });
  params.append('c', callbackName);

  const script = document.createElement('script');
  let settled = false;

  const cleanup = () => {
    if (script.parentNode) script.parentNode.removeChild(script);
    try { delete window[callbackName]; } catch (_) { window[callbackName] = undefined; }
  };

  const timeoutId = window.setTimeout(() => {
    if (settled) return;
    settled = true;
    cleanup();
    nativeSubmit(form);
  }, JSONP_TIMEOUT_MS);

  window[callbackName] = function () {
    if (settled) return;
    settled = true;
    window.clearTimeout(timeoutId);
    cleanup();
    window.location.assign(landing);
  };

  script.onerror = () => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timeoutId);
    cleanup();
    nativeSubmit(form);
  };

  script.src = endpoint + (endpoint.includes('?') ? '&' : '?') + params.toString();
  document.head.appendChild(script);
}

function nativeSubmit(form) {
  form.dataset.etpNlJsonpFallback = '1';
  form.submit();
}

function validate(form) {
  const emailInput = form.querySelector('input[type="email"]');
  const value = emailInput ? emailInput.value.trim() : '';

  if (!value) {
    return (
      form.dataset.errorEmailEmpty || 'Please enter your email address.'
    );
  }
  if (!EMAIL_REGEX.test(value)) {
    return (
      form.dataset.errorEmailInvalid ||
      'Please enter a valid email address.'
    );
  }

  const checkboxes = form.querySelectorAll('input[type="checkbox"]');
  if (checkboxes.length > 0) {
    const checked = form.querySelectorAll('input[type="checkbox"]:checked');
    if (checked.length === 0) {
      return (
        form.dataset.errorNoSelection ||
        'Please select at least one newsletter.'
      );
    }
  }

  return null;
}

function showError(form, text) {
  let msg = form.querySelector('.etp-newsletter-form-error');
  if (!msg) {
    msg = document.createElement('p');
    msg.className = 'etp-newsletter-form-error';
    msg.setAttribute('role', 'alert');

    const emailInput = form.querySelector('input[type="email"]');
    const anchor =
      emailInput?.closest(
        '.etp-nsp__email-row, .mc4wp-form-fields, .mc4wp-form-fields__row'
      ) || emailInput;

    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(msg, anchor);
    } else {
      form.prepend(msg);
    }
  }
  msg.textContent = text;
}

function clearError(form) {
  const msg = form.querySelector('.etp-newsletter-form-error');
  if (msg) msg.remove();
}
